// External Imports
import { useRef, useState, useContext, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import Heading from "../UI/Heading";
import { UtilityContext } from "../../context/util-context";
import VehicleSelector from "../vehicles-page/VehicleSelector";
import CustomerSelector from "../customers-page/CustomerSelector";
import { DataContext } from "../../context/data-context";
import SelectPair from "../UI/SelectPair";
import LoadingSpinnerWrapper from "../UI/LoadingSpinnerWrapper";
import DetailPair from "../UI/DetailPair";

/** ========================================================================
 * * Add Booking Page
 * Renders a page, which allows the admin to create a new booking.
 * ========================================================================== */

export default function AddBooking() {
  // Load Context
  const { navigate, setWarning } = useContext(UtilityContext);
  const { locations } = useContext(DataContext);

  /*
   * Customer and Vehicle IDs can be provided in the URL query params,
   * which allows auto-filling the form.
   */
  const [searchParams] = useSearchParams();

  // Find customer provided in the URL query params
  const customerId = searchParams.get("customerId");
  // Find vehicle provided in the URL query params
  const vehicleId = searchParams.get("vehicleId");

  // Initialize states
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState();
  const [vehicle, setVehicle] = useState();

  /* 
  * Declare refs 
  Refs are used to updated values in the form fields.
  */
  const bookingFeeRef = useRef();
  const deliveryFeeRef = useRef();
  const taxesRef = useRef();
  const totalFeeRef = useRef();

  /* ======================= CALCULATE TOTAL FEE ======================= */
  const calculateTotalFee = () => {
    const totalFee =
      Number(bookingFeeRef.current.value) +
      Number(deliveryFeeRef.current.value) +
      Number(taxesRef.current.value);
    totalFeeRef.current.value = "$" + totalFee.toFixed(2);
  };

  /* ======================= FETCH PRICE ======================= */
  const fetchPrice = async () => {
    const payload = {
      vehicleId: vehicle.id,
      customerId: customer.id,
      startDate: document.getElementById("startDate").value,
      startTime: document.getElementById("startTime").value,
      endDate: document.getElementById("endDate").value,
      endTime: document.getElementById("endTime").value,
    };
    let flag = true;
    for (const key of Object.keys(payload)) {
      if (payload[key] === "") {
        flag = false;
        break;
      }
    }
    if (!flag) return;
    try {
      setLoading(true);
      // Send API request
      const price = await requestAdminAPI("fetch-price", payload);

      // Update Inputs
      document.getElementById("bookingFee").value = price.subtotal;
      document.getElementById("taxes").value = price.taxes;
      document.getElementById("total").value = price.total;
      document.getElementById("mileageRate").value = price.mileageRate;
      document.getElementById("milesIncluded").value = price.milesIncluded;
      if (customer.plan) document.getElementById("deposit").value = 0;
      calculateTotalFee();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= UPDATE SELECTED VEHICLE ======================= */
  const selectVehicle = async (vehicle) => {
    try {
      setLoading(true);
      // Update the form fields with the vehicle's data
      document.getElementById("pickUpLocation").value = locations.find(
        (l) => l.id === vehicle.locationId
      ).address;
      document.getElementById("dropOffLocation").value = locations.find(
        (l) => l.id === vehicle.locationId
      ).address;
      document.getElementById("deliveryFee").value = "0.00";

      /*
       * Vehicle report includes mileage and fuel level,
       * which is auto-filled in the form fields.
       */
      const response = await requestAdminAPI("fetch-vehicle-reports", {
        vehicles: [{ vin: vehicle.vin, trackerId: vehicle.trackerId }],
      });
      const report = response[0]?.report;

      // Update the form fields with the vehicle's report data
      if (report) {
        // Update mileage
        document.getElementById("startMileage").value = report.mileage;
        // Update fuel level
        document.getElementById("startFuel").value = report.fuel;
      }
      setVehicle(vehicle);
      setLoading(false);
    } catch (err) {
      // Reset the form fields
      document.getElementById("startMileage").value = undefined;
      document.getElementById("startFuel").value = undefined;
      setVehicle(vehicle);
      setLoading(false);
    }
  };

  /* ======================= CREATE BOOKING HANDLER ======================= */
  const createBookingHandler = async () => {
    setLoading(true);

    try {
      const payload = {
        customerId: customer.id,
        vehicleId: vehicle.id,
        startDate: document.getElementById("startDate").value,
        endDate: document.getElementById("endDate").value,
        startTime: document.getElementById("startTime").value + ":00",
        endTime: document.getElementById("endTime").value + ":00",
        pickUpLocation: document.getElementById("pickUpLocation").value,
        dropOffLocation: document.getElementById("dropOffLocation").value,
        startMileage: document.getElementById("startMileage").value || 0,
        startFuel: document.getElementById("startFuel").value || 0,
        milesIncluded: document.getElementById("milesIncluded").value || 800,
        mileageRate: document.getElementById("mileageRate").value || 0.35,
        deliveryFee: document.getElementById("deliveryFee").value || 0,
        bookingFee: document.getElementById("bookingFee").value || 0,
        taxes: document.getElementById("taxes").value || 0,
        daysUntilDue: document.getElementById("daysUntilDue").value || 3,
        automaticExtension:
          document.getElementById("automaticExtension").value === "Yes",
        deposit: document.getElementById("deposit").value || 0,
      };

      let check = false;
      for (const key of Object.keys(payload)) {
        if (payload[key] === undefined || payload[key] === "")
          check = `${key} is required`;
      }
      if (check) {
        setWarning(check);
        setLoading(false);
        return;
      }

      // Send API request
      const response = await requestAdminAPI("create-booking", payload);

      // Navigate to the booking info page
      navigate(`/bookings/info?id=${response}`);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrice();
  }, [customer, vehicle])

  return (
    <div className="container">
      <Heading
        title="Add Booking"
        text="Selecting a vehicle will update fuel and mileage if a tracker is installed."
      />

      <div className="grid gap-8 mt-6">
        <div className="grid text-sm md:grid-cols-4 gap-x-8 gap-y-4 text-stone-400">
          <CustomerSelector onSelect={setCustomer} selected={customerId} />

          <div className="lg:col-span-2">
            <VehicleSelector onSelect={selectVehicle} selected={vehicleId} />
          </div>
          <SelectPair
            label="Automatic Extension"
            edit={true}
            options={["No", "Yes"]}
            inputOptions={{ required: true, id: "automaticExtension" }}
          />
          <DetailPair
            label="Start Date"
            edit={true}
            inputOptions={{
              type: "date",
              required: true,
              id: "startDate",
              onChange: fetchPrice,
            }}
          />
          <DetailPair
            label="Start Time"
            edit={true}
            inputOptions={{
              type: "time",
              required: true,
              id: "startTime",
              onChange: fetchPrice,
            }}
          />
          <DetailPair
            label="End Date"
            edit={true}
            inputOptions={{
              type: "date",
              required: true,
              id: "endDate",
              onChange: fetchPrice,
            }}
          />
          <DetailPair
            label="End Time"
            edit={true}
            inputOptions={{
              type: "time",
              required: true,
              id: "endTime",
              onChange: fetchPrice,
            }}
          />
          <div className="lg:col-span-2">
            <DetailPair
              label="Pick Up Location"
              edit={true}
              inputOptions={{
                type: "text",
                required: true,
                id: "pickUpLocation",
                placeholder: "Street Address, City, State Zip",
              }}
            />
          </div>
          <div className="lg:col-span-2">
            <DetailPair
              label="Drop Off Location"
              edit={true}
              inputOptions={{
                type: "text",
                required: true,
                id: "dropOffLocation",
                placeholder: "Street Address, City, State Zip",
              }}
            />
          </div>
          <DetailPair
            label="Start Mileage (mi)"
            edit={true}
            inputOptions={{
              type: "number",
              required: true,
              id: "startMileage",
              placeholder: "Default 0",
            }}
          />
          <DetailPair
            label="Start Fuel (%)"
            edit={true}
            inputOptions={{
              type: "number",
              required: true,
              id: "startFuel",
              placeholder: "Default 0",
            }}
          />

          <DetailPair
            label="Miles Included (mi)"
            edit={true}
            inputOptions={{
              type: "number",
              required: true,
              id: "milesIncluded",
              placeholder: "Default 800",
            }}
          />
          <DetailPair
            label="Extra Mileage Rate ($/mi)"
            edit={true}
            inputOptions={{
              type: "number",
              step: "0.01",
              required: true,
              id: "mileageRate",
              placeholder: "Default $0.35/mi",
            }}
          />
          <DetailPair
            label="Booking Fee"
            edit={true}
            inputOptions={{
              type: "number",
              step: "0.01",
              required: true,
              id: "bookingFee",
              ref: bookingFeeRef,
              onChange: calculateTotalFee,
            }}
          />
          <DetailPair
            label="Delivery Fee"
            edit={true}
            inputOptions={{
              type: "number",
              step: "0.01",
              required: true,
              id: "deliveryFee",
              ref: deliveryFeeRef,
              onChange: calculateTotalFee,
            }}
          />
          <DetailPair
            label="Taxes"
            edit={true}
            inputOptions={{
              type: "number",
              step: "0.01",
              required: true,
              id: "taxes",
              ref: taxesRef,
              onChange: calculateTotalFee,
            }}
          />
          <DetailPair
            label="Total"
            edit={true}
            inputOptions={{
              disabled: true,
              id: "total",
              ref: totalFeeRef,
            }}
          />

          <DetailPair
            label="Refundable Deposit"
            edit={true}
            inputOptions={{
              type: "number",
              required: true,
              id: "deposit",
              defaultValue: 500,            
            }}
          />

          <DetailPair
            label="Days Until Due"
            edit={true}
            inputOptions={{
              type: "number",
              required: true,
              id: "daysUntilDue",
              defaultValue: 1,
            }}
          />
        </div>
        <div className="flex items-center space-x-4 text-sm">
          {loading ? (
            <LoadingSpinnerWrapper />
          ) : (
            <button
              onClick={createBookingHandler}
              className="w-full md:w-32 btn-primary"
            >
              Submit
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
